import { useRef, useState } from 'react';
import type { CSSProperties, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from 'qampo-ui-library/base';
import { dateToIsoString, isoStringToDate } from '../../../utils/dateUtils';
import styles from './DateField.module.css';

interface DateFieldProps {
  value: string;
  onChange: (isoDate: string) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
}

// Approx height of the open calendar popup (header + weekday row + 6 week rows).
// Only used to choose flip direction, so a slight over-estimate is safe — it just
// biases toward opening upward when space below the trigger is tight.
const POPUP_HEIGHT_PX = 340;

// Matches the library's default 1px gap between the trigger and the popup.
const POPUP_GAP_PX = 1;

// Wraps the qampo-ui-library calendar DatePicker, adding the label / required /
// error-and-helper chrome the booking forms expect and converting between the
// picker's Date objects and the forms' ISO date strings. Date-order rules (end
// after start, etc.) stay in each form's validate() — DatePicker has no min/max
// — so this control only reports errors, it doesn't constrain selection.
//
// The library DatePicker renders its popup inline (absolutely positioned, always
// downward), so inside a scrollable modal body the overflow clips it. On press we
// measure the trigger and pin the popup with `position: fixed` anchored to the
// viewport — that lifts it out of the modal's overflow so it can't be clipped —
// flipping it above the trigger when there isn't room below.
export const DateField = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  required = false,
  placeholder
}: DateFieldProps) => {
  const { i18n } = useTranslation();
  const pickerSlotRef = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState<CSSProperties | undefined>(undefined);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    // The trigger is always the first child of the DatePicker root; the popup (when
    // open) is a later sibling. Ignore presses on the open popup so selecting a day
    // doesn't recompute the position.
    const pickerRoot = pickerSlotRef.current?.firstElementChild;
    const trigger = pickerRoot?.firstElementChild;
    if (!trigger?.contains(event.target as Node)) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < POPUP_HEIGHT_PX && rect.top > spaceBelow;

    setPopupStyle({
      position: 'fixed',
      left: rect.left,
      top: flipUp ? 'auto' : rect.bottom + POPUP_GAP_PX,
      bottom: flipUp ? window.innerHeight - rect.top + POPUP_GAP_PX : 'auto',
      marginTop: 0,
      marginBottom: 0
    });
  };

  return (
    <div className={styles.wrapper}>
      {label && (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.asterisk}>*</span>}
        </span>
      )}
      <div ref={pickerSlotRef} onPointerDownCapture={handlePointerDown}>
        <DatePicker
          value={isoStringToDate(value)}
          onChange={(date) => onChange(dateToIsoString(date))}
          language={i18n.language}
          placeholder={placeholder}
          styles={{
            input: error ? { borderColor: 'var(--color-error-primary)' } : undefined,
            popup: popupStyle
          }}
        />
      </div>
      {helperText && (
        <span className={error ? styles.errorText : styles.helperText}>{helperText}</span>
      )}
    </div>
  );
};
